// RemindersPage.tsx
import { useEffect, useState } from "react";
import { Typography, Box, CircularProgress, IconButton, Link, Paper } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { Reminder, User } from "../types";  
import Layout from "../components/Layout";

import axiosExt from '../axios';
import { API_BASE_URL } from "../config.ts";
import { useTranslation } from "react-i18next";


const RemindersPage = () => {

    const { t } = useTranslation();

    const [reminders, setReminders] = useState<Reminder[]>([]);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<User[]>([]);
    const navigate = useNavigate();

    const fetchReminders = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const res = await axiosExt.get(`${API_BASE_URL}/orderreminders/`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setReminders(res.data);
        } catch (err) {
            console.error("Failed to load reminders", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        //setLoading(true);

        try {
            const token = localStorage.getItem("token");
            const response = await axiosExt.get<User[]>(`${API_BASE_URL}/users/roles/admin-support`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }
            );
            if (response.status === 401) {
                throw new Error("Unauthorized. Please log in again.");
            }
            setUsers(response.data);
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            //setLoading(false);
        }
    }

    useEffect(() => {
        fetchUsers();
        fetchReminders();
    }, []);


        // Utility to convert UTC → local string
        function formatUtcToLocal(utcString?: string): string {
            if (!utcString) return "";

            const isoString = utcString.endsWith("Z") ? utcString : `${utcString}Z`;
            const date = new Date(isoString);
            if (isNaN(date.getTime())) return "";

            return new Intl.DateTimeFormat(undefined, {
                dateStyle: "medium",
                timeStyle: "short",
            }).format(date);
        }

    const handleDismiss = async (id: string) => {
        setReminders(prev =>
            prev.map(r => (r.id === id ? { ...r, isDismissed: true } : r))
        );
        const token = localStorage.getItem("token");

        await axiosExt.put(`${API_BASE_URL}/orderreminders/${id}/dismiss`, {}, {
            headers: { Authorization: `Bearer ${token}` },
        });

        // maybe refresh the reminders list here
        await fetchReminders();
    };

    const columns: GridColDef[] = [
        {
            field: "reminderDateTimeUtc",
            headerName: t("Reminder Time"),
            flex: 1,
            valueGetter: (params) => formatUtcToLocal(params),
        },
        {
            field: "snoozedUntilUtc",
            headerName: t("Snooze Time"),
            flex: 1,
            valueGetter: (params) => formatUtcToLocal(params),
        },
        {
            field: "userId", headerName: t("User"), width: 100, valueGetter: (params) => {
                const user = users.find(u => u.id === params);
                return user ? user.userName : '';
            },
        },
        {
            field: "note", headerName: t("Note"), flex: 2 },
        {
            field: "orderId",
            headerName: t("Order"),
            flex: 1,
            renderCell: (params) => (
                <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate(`/orderdetailspage/${params.value}?view=list/`)}
                >
                    {t("View")}
                </Link>
            ),
        },
        {
            field: "actions",
            headerName: "",
            sortable: false,
            renderCell: (params) => (
                <IconButton
                    color="error"
                    onClick={() => handleDismiss(params.row.id)}
                >
                    <DeleteIcon />
                </IconButton>
            ),
        },
    ];

    const visibleReminders = reminders.filter(r => !r.isDismissed);

    return (
        <Layout>

        <Box p={3}>
            <Typography variant="h5" gutterBottom>
                Reminders
            </Typography>

            {loading ? (
                <CircularProgress />
            ) : (
                <Paper style={{ height: 500, width: "100%" }}>
                    <DataGrid
                        rows={visibleReminders}
                        columns={columns}
                        getRowId={(row) => row.id}
                        pageSizeOptions={[5, 10, 20]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                    />
                </Paper>
            )}
            </Box>

        </Layout>

    );
};

export default RemindersPage;
